import {
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { Thunder } from "@certa/icons/components/Thunder";
import styles from "./PreviewActions.module.css";
import { IconContainer } from "./Logo";
import { useNavigate } from "react-router";
import type {
  DataObjectInstance,
  DataObjectType
} from "@certa/queries/types/dataObjectsAPIs.types";
import { useIntl } from "react-intl";
import { useGetDataObjectWorkflowRelationActions } from "@certa/queries/hooks/dataObjects.hooks";
import { useCreateWorkflowMutation } from "@certa/queries/hooks/workflow.hooks";
import { useDataObjectActionsAllowedKinds } from "@certa/common/hooks/useDataObjectActionsAllowedKinds";
import {
  Toast,
  ToastPlacements,
  ToastTypes
} from "@certa/catalyst/components/Toast";
import type { Kind } from "@certa/types";
import { AnalyticsEvents, analyticsService } from "@certa/analytics";
import type { IconProps } from "@certa/icons";
import { handleWorkflowCreationError } from "@certa/common/utils/handleWorkflowCreationError";

const PREVIEW_ACTION_ICON_SIZE = 14;

type ActionsItemsWrapperProps = {
  children: React.ReactNode;
  ariaLabel: string;
  title: string;
};

export const ActionsItemsWrapper = ({
  children,
  ariaLabel,
  title
}: ActionsItemsWrapperProps) => {
  return (
    <Stack gap="12" padding="0 0 12 0" role="group" aria-label={ariaLabel}>
      <Typography
        variant={TypographyVariants.LABEL_SM_BOLD}
        color={TypographyColors.NEUTRAL_700}
      >
        {title}
      </Typography>
      {children}
    </Stack>
  );
};

type ActionButtonProps = {
  label: string;
  icon: (props: IconProps) => JSX.Element;
  onClick: () => void;
};

export const ActionButton = ({
  onClick,
  label,
  icon: Icon
}: ActionButtonProps) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") onClick();
  };

  return (
    <Stack
      direction="horizontal"
      gap="8"
      dangerouslySetClassName={styles.actionButton}
      aria-label={label}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <IconContainer
        backgroundColor="var(--colors-certa-brand-100)"
        icon={
          <Icon size={PREVIEW_ACTION_ICON_SIZE} className={styles.actionIcon} />
        }
      />
      <Typography
        variant={TypographyVariants.LABEL_SM}
        color={TypographyColors.NEUTRAL_700}
      >
        {label}
      </Typography>
    </Stack>
  );
};

type DataObjectActionsProps = {
  objectInstance: DataObjectInstance;
  objectType: DataObjectType;
};

const DataObjectActions = ({
  objectInstance,
  objectType
}: DataObjectActionsProps) => {
  const navigate = useNavigate();
  const intl = useIntl();

  const { data: objectTypeWorkflowActions, status } =
    useGetDataObjectWorkflowRelationActions({
      objectTypeTag: objectType.tag,
      objectUid: objectInstance.uid
    });

  const { mutate: createWorkflow, isLoading: isCreatingWorkflow } =
    useCreateWorkflowMutation();

  const allowedKinds = useDataObjectActionsAllowedKinds(
    objectInstance,
    objectType,
    objectTypeWorkflowActions
  );

  const allowedKindsBasedOnStatus = allowedKinds?.filter(kind => {
    const statusTag = objectInstance.status?.tag;

    const objectTypeCurrentKindAction = objectTypeWorkflowActions?.find(
      action => action.workflowKindTag === kind.tag && !action.isDisabled
    );

    if (objectTypeCurrentKindAction?.allowedStatusTags !== undefined) {
      return (
        statusTag &&
        objectTypeCurrentKindAction.allowedStatusTags.includes(statusTag)
      );
    }

    return true;
  });

  const handleWorkflowKindClick = (workflowKind: Kind) => {
    if (isCreatingWorkflow) {
      return;
    }
    analyticsService.track(
      AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_PREVIEW_ACTION_CLICK,
      {
        result_type: "object",
        content_type: "start_workflow",
        certa_version: "OS3"
      }
    );

    createWorkflow(
      {
        kind: workflowKind.tag,
        name: workflowKind.name,
        parent: null,
        object_id: objectInstance.id.toString()
      },
      {
        onSuccess: response => {
          navigate(`/process/${response.id}?new=true`);
        },
        onError: async error => {
          await handleWorkflowCreationError(error, navigate, intl);
        }
      }
    );
  };

  if (status !== "success" || !allowedKindsBasedOnStatus?.length) {
    return null;
  }

  const startNewWorkflowLabel = intl.formatMessage({
    id: "globalSearch.startNewWorkflowMessage",
    defaultMessage: "Start New Workflow"
  });

  const preparingProcessLabel = intl.formatMessage({
    id: "commonTextInstances.preparingProcess",
    defaultMessage: "Preparing process"
  });

  return (
    <>
      <Toast
        title={`${preparingProcessLabel}...`}
        show={isCreatingWorkflow}
        type={ToastTypes.LOADING}
        placement={ToastPlacements.BOTTOM_RIGHT}
        shouldAutoDismiss={false}
      />
      <ActionsItemsWrapper
        ariaLabel={startNewWorkflowLabel}
        title={startNewWorkflowLabel}
      >
        {allowedKindsBasedOnStatus.map(allowedKind => (
          <ActionButton
            key={allowedKind.id}
            label={allowedKind.name}
            icon={Thunder}
            onClick={() => handleWorkflowKindClick(allowedKind)}
          />
        ))}
      </ActionsItemsWrapper>
    </>
  );
};

export { DataObjectActions };
